import React, { useEffect, useState, useCallback, memo } from "react";
import { Dropdown, Form, Spinner } from "react-bootstrap";
import { ChevronDown } from "lucide-react";
import fill_chevron_down from "../../assets/Vehicles/fill_chevron_down.png";
import close from "../../assets/Vehicles/close_filter.png";
import { getApi } from "./http-clients";

const CommonDropdown = ({
  apiUrl,
  placeholder = "Select",
  onSelect,
  value,
  searchView = false,
}) => {
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- Fetch Data ---
  const fetchData = useCallback(
    async (term = searchTerm) => {
      setLoading(true);
      try {
        if (apiUrl) {
          const response = await getApi(apiUrl, { term });
          if (response.status === 200 && response.data.success) {
            setItems(response.data.suggestionlist);
          } else {
            setItems([]);
          }
        }
      } catch (err) {
        console.error("Error fetching options:", err);
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, searchTerm]
  );

  // --- Effects ---

  // Initial fetch
  useEffect(() => {
    fetchData("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Keep empty dependency to run only on mount

  // Sync selected value from parent
  useEffect(() => {
    setSelected(value);
  }, [value]);

  // --- Handlers ---

  const handleSearch = useCallback(
    (e) => {
      const term = e.target.value;
      setSearchTerm(term);
      fetchData(term);
    },
    [fetchData]
  );

  const handleSelect = useCallback(
    (item) => {
      setSelected(item);
      if (onSelect) onSelect(item);
    },
    [onSelect]
  );

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      handleSelect(null);
    },
    [handleSelect]
  );

  // --- Styles ---
  const toggleClass =
    searchView === false
      ? "bg-transparent text-black"
      : "bg-white text-purple assigned-dropdown rounded-4 px-4";

  return (
    <Dropdown className="w-100">
      <Dropdown.Toggle
        variant="success"
        id="dropdown-basic"
        className={`${toggleClass} w-100 d-flex justify-content-between dp-toggle`}
      >
        <span
          style={{
            textOverflow: "ellipsis", // Fixed typo 'extOverflow'
            whiteSpace: "nowrap",
            overflow: "hidden",
            color: selected ? "#212129" : "#888888",
          }}
        >
          {selected ? selected.label : placeholder}
        </span>
        <span className="text-black d-flex align-items-center gap-2">
          {searchView === false ? (
            <ChevronDown size={20} />
          ) : (
            <img src={fill_chevron_down} alt="chev down" />
          )}
          {searchView === true && selected && (
            <img
              onClick={handleClear}
              src={close}
              alt="x"
              style={{ cursor: "pointer" }}
            />
          )}
        </span>
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="p-2 w-100"
        style={{ maxHeight: "250px", overflowY: "auto" }}
      >
        {/* Search box */}
        <Form.Control
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearch}
          className="mb-2"
          onClick={(e) => e.stopPropagation()}
        />
        {/* Added stopPropagation to prevents menu closing on click input */}

        {/* Loading spinner */}
        {loading && (
          <div className="px-2 text-muted">
            <Spinner size="sm" /> Loading...
          </div>
        )}

        {/* Dropdown items */}
        {!loading && items.length > 0 ? (
          items.map((item) => (
            <Dropdown.Item
              key={item.value}
              onClick={() => handleSelect(item)}
              className="text-black"
            >
              {item.label}
            </Dropdown.Item>
          ))
        ) : !loading ? (
          <div className="px-2 text-muted">No results found</div>
        ) : null}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default memo(CommonDropdown);
